import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StatsCard from '../components/dashboard/StatsCard';

const useQuery = () => {
    return new URLSearchParams(window.location.search);
}

const DriverSpecificExpenses = () => {
    const { isDark } = useTheme();
    const query = useQuery();
    const driverId = query.get('driver');
    const [stats, setStats] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (driverId) {
            fetchData();
        }
    }, [driverId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsResponse, expensesResponse] = await Promise.all([
                fetch(`/api/v1/driver_stats/${driverId}`),
                fetch(`/api/v1/expenses?driver_id=${driverId}`)
            ]);

            const statsData = await statsResponse.json();
            const expensesData = await expensesResponse.json();

            setStats(statsData);
            setExpenses(expensesData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: {
            padding: '2rem',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            color: isDark ? '#fff' : '#1f2937'
        },
        header: {
            marginBottom: '2rem'
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem'
        },
        subTitle: {
            fontSize: '1.25rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            marginBottom: '1rem'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        tableContainer: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            overflowX: 'auto',
            padding: '1rem'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap'
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.5rem'
        }
    };

    if (loading) return <div style={styles.loading}>Loading expenses data...</div>;
    if (!stats) return <div style={styles.loading}>Driver not found</div>;

    const { driver, car } = stats;
    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    {driver.first_name} {driver.last_name}
                </h1>
                <div style={styles.subTitle}>
                    {car?.model} - {car?.car_number}
                </div>
                {(car?.image_url || car?.car_image_link) && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <img
                            src={car.image_url || car.car_image_link}
                            alt={`${car.model} - ${car.car_number}`}
                            style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '1rem',
                                objectFit: 'cover',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                        />
                    </div>
                )}
            </div>

            <div style={styles.statsGrid}>
                <StatsCard
                    title="Total Expenses"
                    value={`GHS ${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon="💸"
                    color="red"
                />
            </div>

            <h2 style={{ ...styles.title, fontSize: '1.5rem', marginBottom: '1rem' }}>Expenses History</h2>

            <div style={styles.tableContainer}>
                {expenses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>No expenses records found</div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Period Start</th>
                                <th style={styles.th}>Period End</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Frequency</th>
                                <th style={styles.th}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((expense) => (
                                <tr key={expense.id}>
                                    <td style={styles.td}>{expense.period_start}</td>
                                    <td style={styles.td}>{expense.period_end || '-'}</td>
                                    <td style={styles.td}>{expense.expense_type}</td>
                                    <td style={styles.td}>{expense.expense_mode || '-'}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>GHS {parseFloat(expense.amount).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DriverSpecificExpenses;
